<template>
    <div class="cancel-modal-question">
        <div class="question-box">
            <transition-group
                name="fade"
                mode="out-in"
            >
                <div v-if="loading" key="loader">
                    <i class="fas fa-spinner fa-spin loader" />
                </div>
                <error v-else-if="hasRequestError" key="error" />
                <question-content
                    v-else
                    key="question"
                    ref="question"
                    :control="controls[step - 1]"
                    :step="step"
                />
            </transition-group>
            <div class="question-controls">
                <base-submit-button
                    class="btn btn-primary"
                    :loading="formProcessing"
                    @click="saveAnswer"
                >
                    <template v-if="totalSteps !== step">
                        Next Question
                    </template>
                    <template v-else>
                        Thanks!
                    </template>
                </base-submit-button>
                <span
                    v-if="totalSteps !== step"
                    class="cancel-link"
                    @click="nextQuestion({
                        type: controls[step - 1].name,
                        value: false,
                    })"
                >
                    Skip
                </span>
            </div>
        </div>
        <register-testimonial />
    </div>
</template>

<script>
import apiCall from '@/utils/apiCall';
import Error from '@/components/Dashboard/Onboarding/Error';
import { mapMutations, mapActions, mapState } from 'vuex';
import RegisterTestimonial from '@/components/Register/RegisterTestimonial';
import QuestionContent from './QuestionContent.vue';
import toasts from '@/mixins/toasts';
import { remove } from 'lodash';

export default {
    components: {
        RegisterTestimonial,
        QuestionContent,
        Error,
    },

    mixins: [toasts],

    props: {
        showsCount: {
            type: Number,
            default: 0,
        },
        showInfo: {
            type: Object,
            required: true,
        },
    },

    data: () => ({
        step: 1,
        formProcessing: false,
        controls: [],
        loading: true,
    }),

    computed: {
        ...mapState([
            'user',
            'hasRequestError',
        ]),

        /**
         *
         * @returns  {string}  Type of shows count filter
         */
        showsExcludeCondition() {
            return 1 >= this.showsCount ? 'singular' : 'multi';
        },

        /**
         *
         * @returns  {number}  Number of steps
         */
        totalSteps() {
            return this.controls.length;
        },
    },

    /**
     * Created hook
     */
    created() {
        apiCall(this, 'get', '/api/user/profile-controls/offboarding')
            .then((response) => {
                if (200 === response.status && response.data) {
                    this.controls = [...this.setControls(response.data, {
                        type: 'shows_count',
                        value: this.showsExcludeCondition,
                    })];

                    this.loading = false;
                } else {
                    this.setRequestError();
                }
            })
            .catch(() => this.setRequestError())
            .finally(() => {
                this.loading = false;
            });
    },

    methods: {
        ...mapActions(['updateUser']),
        ...mapMutations(['setRequestError']),

        /**
         * Proceed to next question
         *
         * @param {object} data Saved question data.
         */
        nextQuestion(data) {
            if (this.totalSteps !== this.step) {
                this.controls = [...this.setControls(this.controls, data)];

                this.step++;
            } else {
                this.$emit('finish');
            }
        },

        /**
         * Save user answer
         */
        async saveAnswer() {
            const { answer } = this.$refs.question._data;
            const metaKey = this.controls[this.step - 1].name;

            this.formProcessing = true;

            try {
                await apiCall(this, 'post', '/api/shows/offboard', {
                    user_id: this.showInfo.userId,
                    show_hash: this.showInfo.hash,
                    show_feed_url: this.showInfo.feedUrl,
                    show_title: this.showInfo.title,
                    [metaKey]: answer,
                });
            } catch (e) {} // eslint-disable-line no-empty

            this.nextQuestion({ type: metaKey, value: answer });
            this.formProcessing = false;
        },

        /**
         * Filters controls based on condition
         *
         * @param   {Array}   data              Array with controls
         * @param   {object}  condition         Condition params
         * @param   {string}  condition.type    Condition type
         * @param   {string}  condition.value   Condition value
         *
         * @returns  {Array}                    Filtered cotrols
         */
        setControls(data, { type, value }) {
            const controlsData = data;

            remove(controlsData, (element) => {
                if (element.data?.conditions?.[type]) {
                    return element.data?.conditions[type] !== value;
                }
            });

            return controlsData;
        },
    },
};
</script>
